import Service from '@ember/service';
import { service } from '@ember/service';
import {
  task,
  timeout,
  waitForEvent,
} from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import tippy, { hideAll } from 'tippy.js';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { registerDestructor } from '@ember/destroyable';

export default class PlaybackService extends Service {
  @service stereo;
  @service feeds;
  @service store;
  @service session;

  @tracked playerIsActive;
  @tracked blockedSound;
  @tracked blockedPrompt;

  constructor() {
    super(...arguments);
    this.addStereoMonitors();
    registerDestructor(this, this.teardown.bind(this));
  }

  addStereoMonitors() {
    this.stereo.on('audio-played', this.onAudioPlayed.bind(this));
    this.stereo.on('audio-blocked', this.onAudioBlocked.bind(this));
  }

  teardown() {
    if (!this.stereo.isDestroyed) {
      try {
        this.stereo.off('audio-played', this.onAudioPlayed.bind(this));
        this.stereo.off('audio-blocked', this.onAudioBlocked.bind(this));
      } catch (e) {
        // no op
      }
    }
  }

  onAudioPlayed() {
    this.playerIsActive = true;
  }

  onAudioBlocked({ sound }) {
    this.blockedSound = sound;
    this.handleAudioBlocked.perform(sound);

    this.stereo.on('audio-played', () => {
      this.playerIsActive = true;
      this.blockedSound = null;
      if (this.blockedPrompt) {
        hideAll();
        this.blockedSound = null;
        this.blockedPrompt = null;
      }
    });
  }

  stopAndClose() {
    this.stereo.stop();
    this.playerIsActive = false;
  }

  handleAudioBlocked = task({ restartable: true }, async sound => {
    let query = `[data-identifier="${normalizeIdentifier(
      sound.url,
    )}"] .dubbletrack-player-controls`;
    var elements;
    var waitLength = 0;
    var pauseSize = 200;
    while (true) {
      elements = document.querySelectorAll(query);
      waitLength = waitLength + pauseSize;

      await timeout(pauseSize);
      if ((elements && elements.length > 0) || waitLength > 3000) {
        break;
      }
    }

    let instances = tippy(elements, {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '200px',
      animation: 'shift-away-subtle',
      hideOnClick: true,
      interactive: false,
      content:
        'Your browser blocked our autoplay attempt, so give this button a click to listen',
      trigger: 'manual',
      allowHTML: true,
    });

    this.blockedPrompt = instances[0];
    this.blockedPrompt?.show();
    await timeout(1000);
  });

  waitForNextPlayTask = task({ restartable: true }, async sound => {
    let loadedAirings = this.store.peekAll('airing');
    let time = sound.metadata.endsAt;
    let nextAiring = _.sortBy(
      loadedAirings.filter((a) => {
        return (
          a.endsAt > time &&
          a.endsAt.getTime() - time.getTime() < 1000 * 60 &&
          !a.isOverridden
        );
      }),
      ['startsAt'],
    )[0];

    if (!nextAiring) {
      nextAiring = await this.store.queryRecord('airing', {
        filter: {
          ends_at: {
            gt: time.toISOString(),
          },
          active: true,
        },
        include: ['show', 'broadcast'],
        sort: 'starts_at',
        page: { size: 1, number: 1 },
      });
    }

    await waitForEvent(sound, 'audio-ended');

    if (nextAiring) {
      this.playAiringTask.perform(nextAiring);
    }
  });
}
